import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { getApiAsync } from "../../../../api";
import { WorkAuthor } from "../../../../api/graphql";

type WorkAuthorPanelProps = { parentId: string };

export const WorkAuthorPanel: React.FC<WorkAuthorPanelProps> = ({
  parentId,
}) => {
  const rootStore = useRootStore();
  // Extraídos del rootStore para que ESLint vea la cadena de deps del useEffect.
  const { langStore, authStore, activityStore } = rootStore;
  const [items, setItems] = useState<WorkAuthor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        if (cancelled) return;
        const api = await getApiAsync(authStore, activityStore);
        const result = await api.workAuthorListWithOffset({
          where: { parent_MusicalWork_authors: { id: { equals: parentId } } },

          take: 100,
          offset: 0,
        });
        if (!cancelled) {
          setItems(result.workAuthorListWithOffset as WorkAuthor[]);
        }
      } catch (error) {
        console.error("Failed to load workAuthor:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [parentId, authStore, activityStore]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
      >
        {langStore.WorkAuthorLabels?.p || "WorkAuthor"}
        {loading && <CircularProgress size={16} />}
        {!loading && items.length === 0 && (
          <Typography component="span" variant="body2" color="text.secondary">
            ({langStore.noRecordsMessage})
          </Typography>
        )}
      </Typography>
      {!loading && items.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {langStore.WorkAuthorAuthorLabel || "author"}
              </TableCell>
              <TableCell>
                {langStore.WorkAuthorLyricsPercentageLabel ||
                  "lyricsPercentage"}
              </TableCell>
              <TableCell>
                {langStore.WorkAuthorMusicPercentageLabel || "musicPercentage"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <TableRow
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    rootStore.openDrawerView("workAuthor", item.id);
                  }}
                >
                  <TableCell>{item.author?.displayId || "-"}</TableCell>
                  <TableCell>{String(item.lyricsPercentage ?? "-")}</TableCell>
                  <TableCell>{String(item.musicPercentage ?? "-")}</TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
